import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTieneRol } from "@/lib/sesion";
import { AlertCircle, Building2, CheckCircle2, ClipboardList, FileText, Loader2, PlayCircle, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ESTATUS_LABEL, type MiRegistroLegalResumen } from "./types";

interface ContextoMio {
  asociacionId: string | null;
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  tipoAutorizacion: string;
  sectoresCodigos: string[];
  vigenciaFin: string | null;
  cartaSinVencimiento: boolean | null;
}

const TIPO_AUT_LABEL: Record<string, string> = {
  REPRESENTANTE_DIRECTO: "Representante directo",
  INTEGRAL: "Tramitador integral",
  RESTRINGIDA: "Tramitador restringido",
  OPERATIVA: "Tramitador operativo",
  INTERNO_TITULAR: "Autorizado interno",
  GESTION: "Autorizado (gestión)",
  CONSULTA: "Autorizado (consulta)",
  SEGUIMIENTO: "Autorizado (seguimiento)",
  FIRMA: "Autorizado (firma)",
};

const ROLES_CALIDAD = ["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"];

/**
 * Entry del autoservicio del cliente. Carga GET /api/mi-registro-legal y
 * decide qué mostrar según el estatus del RL del usuario en sesión:
 *  - 204 (no tiene RL): tarjeta de bienvenida + "Iniciar trámite".
 *  - BORRADOR_INTERNO / OBSERVADO: tarjeta con progreso + "Continuar trámite".
 *  - EN_REVISION_QA: mensaje "tu solicitud está en revisión".
 *  - APROBADO / ACTIVO: folio asignado + acceso al expediente.
 *  - RECHAZADO: motivo + posibilidad de iniciar trámite nuevo.
 */
export function MiRegistroLegalPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Calidad/Admin no debería caer aquí (esta página es para clientes que tienen
  // su propio RL en captura). Si por alguna navegación errática llegó, lo
  // mandamos a su bandeja en Trámites Legales.
  //
  // El rol se lee de la identidad EFECTIVA: leerlo del token expulsaba de esta
  // pantalla al usuario suplantado, porque el token seguía siendo el del
  // administrador y la redirección lo dejaba en una sección que ese usuario
  // tampoco puede ver.
  const esCalidad = useTieneRol(...ROLES_CALIDAD);
  useEffect(() => {
    if (esCalidad) navigate("/tramites-legales", { replace: true });
  }, [esCalidad, navigate]);
  if (esCalidad) return null;

  const qMiRl = useQuery<MiRegistroLegalResumen | null>({
    queryKey: ["mi-registro-legal"],
    queryFn: async () => {
      const r = await api.get<MiRegistroLegalResumen>("/api/mi-registro-legal", {
        validateStatus: (s) => s === 200 || s === 204,
      });
      return r.status === 204 ? null : r.data;
    },
  });

  const crearBorrador = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ id: string }>("/api/mi-registro-legal/borrador");
      return data.id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["mi-registro-legal"] });
      navigate(`/mi-registro-legal/wizard?id=${id}&paso=1`);
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  if (qMiRl.isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fg-subtle">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Cargando tu Registro Legal…
      </div>
    );
  }

  const rl = qMiRl.data;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-fg">Mi Registro Legal</h1>
        <p className="text-sm text-fg-muted">
          Captura y mantén el expediente legal de tu empresa con NPMX.
        </p>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!rl && <SinRl loading={crearBorrador.isPending} onIniciar={() => crearBorrador.mutate()} />}

      {rl && rl.estatus === "BORRADOR_INTERNO" && (
        <BorradorCard rl={rl} onContinuar={() => navigate(`/mi-registro-legal/wizard?id=${rl.id}&paso=${rl.pasoActual}`)} />
      )}

      {rl && rl.estatus === "OBSERVADO" && (
        <ObservadoCard rl={rl} onAtender={() => navigate(`/mi-registro-legal/wizard?id=${rl.id}&paso=${rl.pasoActual}`)} />
      )}

      {rl && rl.estatus === "EN_REVISION_QA" && <EnRevisionCard rl={rl} />}

      {rl && (rl.estatus === "APROBADO" || rl.estatus === "ACTIVO") && (
        <>
          <AprobadoCard rl={rl} />
          <EmpresasVinculadasCard />
        </>
      )}

      {rl && rl.estatus === "RECHAZADO" && (
        <RechazadoCard
          loading={crearBorrador.isPending}
          onIniciarNuevo={() => crearBorrador.mutate()}
        />
      )}
    </div>
  );
}

function SinRl({ onIniciar, loading }: { onIniciar: () => void; loading: boolean }) {
  return (
    <Card>
      <div className="flex items-start gap-4">
        <ClipboardList className="h-10 w-10 shrink-0 text-primary-strong" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-fg">Inicia tu trámite</h2>
          <p className="mt-1 text-sm text-fg-muted">
            El Registro Legal es el primer paso para acceder a los servicios de certificación de NPMX.
            El proceso toma 4 pasos: datos de la empresa, domicilio fiscal, representantes legales y
            documentación. Puedes guardar tu avance y continuar después.
          </p>
          <button
            type="button"
            onClick={onIniciar}
            disabled={loading}
            className="mt-4 inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            <PlayCircle className="h-4 w-4" />
            {loading ? "Creando borrador…" : "Iniciar trámite"}
          </button>
        </div>
      </div>
    </Card>
  );
}

function BorradorCard({ rl, onContinuar }: { rl: MiRegistroLegalResumen; onContinuar: () => void }) {
  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <ChipEstatus estatus={rl.estatus} />
        <span className="text-xs text-fg-subtle">Iniciado el {fechaCorta(rl.fechaCreacion)}</span>
      </div>
      <h2 className="text-lg font-semibold text-fg">Continúa donde quedaste</h2>
      <p className="mt-1 text-sm text-fg-muted">
        Estás en el paso <strong>{rl.pasoActual}</strong> de <strong>4</strong>. Puedes avanzar, retroceder y guardar tu
        avance en cualquier momento.
      </p>
      <Progreso paso={rl.pasoActual} />
      <button
        type="button"
        onClick={onContinuar}
        className="mt-4 inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
      >
        <Send className="h-4 w-4" />
        Continuar trámite
      </button>
    </Card>
  );
}

function ObservadoCard({ rl, onAtender }: { rl: MiRegistroLegalResumen; onAtender: () => void }) {
  return (
    <Card tone="amber">
      <div className="mb-3 flex items-center gap-2">
        <ChipEstatus estatus={rl.estatus} />
      </div>
      <h2 className="text-lg font-semibold text-fg">Tienes observaciones por atender</h2>
      <p className="mt-1 text-sm text-fg-muted">
        Calidad encontró puntos que necesitan ajuste. Entra al expediente para verlos, corregir tu información
        y reenviar el trámite a revisión.
      </p>
      <button
        type="button"
        onClick={onAtender}
        className="mt-4 inline-flex items-center gap-2 rounded bg-action-warn px-4 py-2 text-sm font-medium text-white transition hover:bg-action-warn-hover"
      >
        Atender observaciones
      </button>
    </Card>
  );
}

function EnRevisionCard({ rl }: { rl: MiRegistroLegalResumen }) {
  return (
    <Card tone="blue">
      <div className="mb-3 flex items-center gap-2">
        <ChipEstatus estatus={rl.estatus} />
        <span className="text-xs text-fg-subtle">Enviado el {fechaCorta(rl.fechaEnvioRevision)}</span>
      </div>
      <h2 className="text-lg font-semibold text-fg">Tu solicitud está en revisión</h2>
      <p className="mt-1 text-sm text-fg-muted">
        Calidad NPMX está revisando tu información. Te notificaremos cuando haya respuesta. Si en 5 días hábiles
        no recibes respuesta, escríbenos.
      </p>
    </Card>
  );
}

function AprobadoCard({ rl }: { rl: MiRegistroLegalResumen }) {
  return (
    <Card tone="green">
      <div className="mb-3 flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-estado-ok" />
        <ChipEstatus estatus={rl.estatus} />
      </div>
      <h2 className="text-lg font-semibold text-fg">¡Tu Registro Legal está aprobado!</h2>
      <p className="mt-1 text-sm text-fg-muted">
        Ya puedes iniciar trámites de certificación con NPMX.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <Dato label="Folio asignado" valor={rl.folioRegistro ?? "—"} mono />
        <Dato label="Razón social" valor={rl.razonSocial} />
        <Dato label="RFC" valor={rl.rfc} mono />
        <Dato label="Estatus" valor={ESTATUS_LABEL[rl.estatus]} />
      </div>
    </Card>
  );
}

function EmpresasVinculadasCard() {
  const q = useQuery<ContextoMio[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<ContextoMio[]>("/api/contextos/mios")).data,
    staleTime: 30_000,
  });

  if (q.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-sm text-fg-subtle">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando empresas vinculadas…
        </div>
      </Card>
    );
  }

  const ctxs = q.data ?? [];

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-primary-strong" />
        <h2 className="text-lg font-semibold text-fg">Empresas vinculadas</h2>
        <span className="ml-1 rounded-full bg-estado-block-bg px-2 py-0.5 text-xs font-medium text-estado-block">
          {ctxs.length}
        </span>
      </div>
      <p className="text-sm text-fg-muted">
        Empresas para las que puedes operar trámites. Selecciona la empresa activa
        en el selector de la barra superior antes de iniciar una solicitud.
      </p>

      {ctxs.length === 0 ? (
        <div className="mt-3 rounded border border-dashed border-edge-strong p-3 text-center text-sm text-fg-subtle">
          Aún no tienes empresas vinculadas. Las asociaciones aparecerán aquí en
          cuanto el área legal de NPMX las registre (representación directa o
          carta poder).
        </div>
      ) : (
        <ul className="mt-3 divide-y rounded border bg-surface">
          {ctxs.map((c) => (
            <li
              key={`${c.titularId}-${c.asociacionId ?? "directo"}`}
              className="flex items-start gap-3 p-3"
            >
              <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-fg-faint" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-fg">{c.titularRazonSocial}</div>
                <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-fg-muted">
                  <BadgeTipoAut tipo={c.tipoAutorizacion} />
                  {c.cartaSinVencimiento ? (
                    <span className="rounded bg-estado-ok-bg px-1.5 py-0.5 text-estado-ok">
                      Sin vencimiento
                    </span>
                  ) : c.vigenciaFin ? (
                    <span className="rounded bg-estado-block-bg px-1.5 py-0.5 text-estado-block">
                      Vence {fechaCorta(c.vigenciaFin)}
                    </span>
                  ) : null}
                </div>
                <SectoresEmpresa ctx={c} />
                {c.tramitadorRazonSocial && (
                  <div className="mt-1 text-[11px] text-fg-subtle">
                    vía tramitador {c.tramitadorRazonSocial}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

interface SectorOption {
  id: string;
  codigo: string;
  nombre: string;
}

/**
 * Sectores habilitados de una empresa vinculada, como chips-toggle.
 * Autogestión del cliente (ajuste 2026-07-12): habilita/deshabilita sectores
 * sin intervención de NPMX; el cambio surte efecto de inmediato (invalida
 * los contextos, que alimentan las validaciones de acceso por sector).
 * D-04 (F4): solo el REPRESENTANTE DIRECTO administra los sectores; los
 * usuarios autorizados y los vinculados vía tramitador los ven en solo lectura.
 */
function SectoresEmpresa({ ctx }: { ctx: ContextoMio }) {
  const qc = useQueryClient();
  const [pendiente, setPendiente] = useState<string | null>(null);
  const [errorSector, setErrorSector] = useState<string | null>(null);
  const puedeAdministrar = ctx.tipoAutorizacion === "REPRESENTANTE_DIRECTO";
  const qSectores = useQuery<SectorOption[]>({
    queryKey: ["catalogo-sectores"],
    queryFn: async () => (await api.get<SectorOption[]>("/api/catalogos/sectores")).data,
    staleTime: 300_000,
  });

  if (!puedeAdministrar) {
    const habilitados = ctx.sectoresCodigos ?? [];
    const nombreDe = (codigo: string) =>
      (qSectores.data ?? []).find((s) => s.codigo === codigo)?.nombre ?? codigo;
    return (
      <div className="mt-1.5">
        <div className="text-[11px] font-medium text-fg-subtle">Sectores autorizados</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {habilitados.length === 0 ? (
            <span className="text-[11px] text-fg-subtle">Sin sectores autorizados.</span>
          ) : (
            habilitados.map((codigo) => (
              <span
                key={codigo}
                className="inline-flex items-center gap-1 rounded-full border border-estado-ok/40 bg-estado-ok-bg px-2 py-0.5 text-[11px] font-medium text-estado-ok"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-estado-ok" />
                {nombreDe(codigo)}
              </span>
            ))
          )}
        </div>
        <div className="mt-1 text-[11px] text-fg-subtle">
          Los sectores los administra el representante legal de la empresa.
        </div>
      </div>
    );
  }

  const toggle = async (codigo: string) => {
    setPendiente(codigo);
    setErrorSector(null);
    const codigos = new Set(ctx.sectoresCodigos ?? []);
    if (codigos.has(codigo)) codigos.delete(codigo);
    else codigos.add(codigo);
    try {
      await api.put(`/api/registros-legales/${ctx.titularId}/sectores`, {
        sectoresCodigos: [...codigos],
      });
      await qc.invalidateQueries({ queryKey: ["contextos-mios"] });
    } catch (e: any) {
      setErrorSector(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo actualizar el sector.",
      );
    } finally {
      setPendiente(null);
    }
  };

  return (
    <div className="mt-1.5">
      <div className="text-[11px] font-medium text-fg-subtle">Sectores habilitados</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {(qSectores.data ?? []).map((s) => {
          const activo = (ctx.sectoresCodigos ?? []).includes(s.codigo);
          const cargando = pendiente === s.codigo;
          return (
            <button
              key={s.id}
              type="button"
              title={activo ? `Deshabilitar ${s.nombre}` : `Habilitar ${s.nombre}`}
              onClick={() => toggle(s.codigo)}
              disabled={pendiente !== null}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition disabled:opacity-60 ${
                activo
                  ? "border-estado-ok/40 bg-estado-ok-bg text-estado-ok"
                  : "border-edge-strong bg-surface text-fg-subtle hover:bg-surface-2"
              }`}
            >
              {cargando
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <span className={`h-1.5 w-1.5 rounded-full ${activo ? "bg-estado-ok" : "bg-edge-strong"}`} />}
              {s.nombre}
            </button>
          );
        })}
      </div>
      {errorSector && (
        <div className="mt-1 text-[11px] font-medium text-estado-error">{errorSector}</div>
      )}
    </div>
  );
}

function BadgeTipoAut({ tipo }: { tipo: string }) {
  const cls =
    tipo === "REPRESENTANTE_DIRECTO"
      ? "bg-estado-info-bg text-estado-info"
      : "bg-estado-warn-bg text-estado-warn";
  return (
    <span className={`rounded px-1.5 py-0.5 font-medium ${cls}`}>
      {TIPO_AUT_LABEL[tipo] ?? tipo}
    </span>
  );
}

function RechazadoCard({ onIniciarNuevo, loading }: { onIniciarNuevo: () => void; loading: boolean }) {
  return (
    <Card tone="rose">
      <div className="mb-3 flex items-center gap-2">
        <ChipEstatus estatus="RECHAZADO" />
      </div>
      <h2 className="text-lg font-semibold text-fg">Tu solicitud fue rechazada</h2>
      <p className="mt-1 text-sm text-fg-muted">
        Calidad determinó que el trámite no procede en su forma actual. Revisa la observación y, si corresponde,
        inicia un nuevo trámite con la corrección.
      </p>
      <button
        type="button"
        onClick={onIniciarNuevo}
        disabled={loading}
        className="mt-4 inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
      >
        <PlayCircle className="h-4 w-4" />
        {loading ? "Creando borrador…" : "Iniciar nuevo trámite"}
      </button>
    </Card>
  );
}

// ---- componentes auxiliares ------------------------------------------------

function Card({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "amber" | "blue" | "green" | "rose" }) {
  const toneClass = {
    neutral: "border-edge bg-surface",
    amber: "border-estado-warn/25 bg-estado-warn-bg",
    blue: "border-estado-info/25 bg-estado-info-bg",
    green: "border-estado-ok/25 bg-estado-ok-bg",
    rose: "border-estado-err/25 bg-estado-err-bg",
  }[tone];
  return <section className={`rounded-lg border p-5 shadow-card ${toneClass}`}>{children}</section>;
}

function ChipEstatus({ estatus }: { estatus: keyof typeof ESTATUS_LABEL }) {
  const colorByEstatus: Record<string, string> = {
    BORRADOR_INTERNO: "bg-estado-block-bg text-estado-block",
    EN_REVISION_QA: "bg-estado-info-bg text-estado-info",
    OBSERVADO: "bg-estado-warn-bg text-estado-warn",
    APROBADO: "bg-estado-ok-bg text-estado-ok",
    ACTIVO: "bg-estado-ok-bg text-estado-ok",
    RECHAZADO: "bg-estado-err-bg text-estado-err",
  };
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${colorByEstatus[estatus] ?? "bg-estado-block-bg text-estado-block"}`}>
      {ESTATUS_LABEL[estatus]}
    </span>
  );
}

function Progreso({ paso }: { paso: number }) {
  const pasos = [
    { n: 1, label: "Datos generales" },
    { n: 2, label: "Domicilio fiscal" },
    { n: 3, label: "Representantes" },
    { n: 4, label: "Revisión y envío" },
  ];
  return (
    <ol className="mt-3 flex items-center gap-1 text-xs">
      {pasos.map((p, i) => (
        <li key={p.n} className="flex items-center gap-1">
          <span
            className={`grid h-6 w-6 place-items-center rounded-full text-xs font-semibold ${
              paso >= p.n ? "bg-primary text-white" : "bg-edge text-fg-subtle"
            }`}
          >
            {p.n}
          </span>
          <span className={`hidden sm:inline ${paso >= p.n ? "text-fg" : "text-fg-subtle"}`}>{p.label}</span>
          {i < pasos.length - 1 && <span className="h-px w-4 bg-edge-strong" />}
        </li>
      ))}
    </ol>
  );
}

function Dato({ label, valor, mono }: { label: string; valor: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className={`text-sm text-fg ${mono ? "font-mono" : ""}`}>{valor}</div>
    </div>
  );
}

function fechaCorta(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

// Re-export por si alguien necesita el ícono fuera
export { FileText };
